import ErrorFallback from 'components/error-fallback/ErrorFallback';
import FontIcon from 'components/icons/FontIcon';
import { CUSTOM_RESULT_KEYS } from 'constants/constants';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FieldContainer } from 'styles/v2/Styles.styled';
import { LcaCustomResultModel, Option } from 'types/types';

import { TextButton } from '../../../buttons';
import CompilerSelectControlledComponent from '../../../epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextAreaComponent from '../../../epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from '../../../epd-wizard-inputs/WizardTextInput';
import { HelpBox } from '../../../help-boxes';
import LcaCustomAdditionalResultsGrid from '../../grids/lca-results/custom/LcaCustomAdditionalResultsGrid';
import LcaCustomBestWorstResultsGrid from '../../grids/lca-results/custom/LcaCustomBestWorstResultsGrid';
import LcaCustomBiogenicResultsGrid from '../../grids/lca-results/custom/LcaCustomBiogenicResultsGrid';
import ValidateErrorsContainer from '../../grids/lca-results/ValidateErrorsContainer';
import LcaGridErrorsContext from '../../grids/lca-results/util/LcaGridErrorsContext';
import ImportAdditionalScenariosLcaResults from '../../panels/import-lca-results/ImportAdditionalScenariosLcaResults';
import ToggleablePanel from '../../panels/toggleable/ToggleablePanel';

type LcaCustomResultPanelProps = {
  customResult: LcaCustomResultModel;
  title: string;
  epdVersionId: string;
  isReadOnly: boolean;
  additionalPerformanceResultsTypes: Option[];
  scenariosLookup: Option[];
  customResultError: any;
  shouldShowTableErrors: boolean;
  onDeleteCustomResult: (id: string) => void;
  onChangedCustomResult: (customResult: LcaCustomResultModel) => (propertyName: string, val: any) => void;
  onShowCustomResultErrors: (customResultId: string) => void;
};

const LcaCustomResultPanel: React.FunctionComponent<LcaCustomResultPanelProps> = ({
  customResult,
  title,
  epdVersionId,
  isReadOnly,
  additionalPerformanceResultsTypes,
  scenariosLookup,
  customResultError,
  shouldShowTableErrors,
  onDeleteCustomResult,
  onChangedCustomResult,
  onShowCustomResultErrors,
}) => {
  const onChanged = onChangedCustomResult(customResult);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ToggleablePanel
        key={customResult.id}
        title={title}
        headerActionsNode={
          !isReadOnly && (
            <TextButton
              type="button"
              text
              icon={<FontIcon icon="delete" />}
              onClick={() => onDeleteCustomResult(customResult.id)}
            />
          )
        }
        style={customResult.resultType ? { overflowX: 'hidden' } : {}}
        error={customResultError}
      >
        <CompilerSelectControlledComponent
          label="Select the type of additional results"
          name="resultType"
          options={additionalPerformanceResultsTypes}
          value={additionalPerformanceResultsTypes.find((x) => x.value == customResult.resultType)}
          onChanged={onChanged}
          placeholder="Select"
          isClearable={false}
          error={customResultError?.resultType}
        />
        {customResult.resultType === CUSTOM_RESULT_KEYS.A4C4 && (
          <FieldContainer>
            <CompilerSelectControlledComponent
              label="Additional scenario"
              name="scenarioId"
              options={scenariosLookup}
              value={scenariosLookup.optionByValue(customResult.scenarioId)}
              onChanged={onChanged}
              placeholder="Select"
              isClearable={false}
              error={customResultError?.scenarioId}
            />
          </FieldContainer>
        )}

        {customResult.resultType === CUSTOM_RESULT_KEYS.BEST && (
          <FieldContainer>
            <WizardTextInputComponent
              label="best-case product"
              name="bestCaseProduct"
              value={customResult.bestCaseProduct}
              onChanged={onChanged}
              maxLength={150}
              error={customResultError?.bestCaseProduct}
            />
          </FieldContainer>
        )}
        {customResult.resultType === CUSTOM_RESULT_KEYS.WORST && (
          <FieldContainer>
            <WizardTextInputComponent
              label="worst-case product"
              name="worstCaseProduct"
              value={customResult.worstCaseProduct}
              onChanged={onChanged}
              maxLength={150}
              error={customResultError?.worstCaseProduct}
            />
          </FieldContainer>
        )}

        {customResult.resultType && (
          <>
            <FieldContainer>
              <WizardTextAreaComponent
                label="Description of the scenario/method"
                placeholder="Type here"
                name="scenarioDescription"
                value={customResult.scenarioDescription}
                onChanged={onChanged}
                error={customResultError?.scenarioDescription}
              />
            </FieldContainer>
            <FieldContainer>
              <HelpBox>
                The description should explain how the additional results differ from the scenario/method of the main
                environmental performance results
              </HelpBox>
            </FieldContainer>
          </>
        )}
        {[
          CUSTOM_RESULT_KEYS.A4C4,
          CUSTOM_RESULT_KEYS.MODELING,
          CUSTOM_RESULT_KEYS.ELECTRICITY,
          CUSTOM_RESULT_KEYS.BIOGAS,
        ].includes(customResult.resultType) && (
          <>
            <ImportAdditionalScenariosLcaResults epdVersionId={epdVersionId} customResultId={customResult.id} />
            <FieldContainer>
              <LcaGridErrorsContext.Provider value={shouldShowTableErrors && customResultError}>
                {customResultError && (
                  <ValidateErrorsContainer onValidate={() => onShowCustomResultErrors(customResult.id)} />
                )}

                <LcaCustomAdditionalResultsGrid
                  epdVersionId={epdVersionId}
                  customResult={customResult}
                  onChangedCustomResult={onChanged}
                />
              </LcaGridErrorsContext.Provider>
            </FieldContainer>
          </>
        )}

        {customResult.resultType === CUSTOM_RESULT_KEYS.BIOGENIC && (
          <FieldContainer>
            <LcaGridErrorsContext.Provider value={shouldShowTableErrors && customResultError}>
              {customResultError && <ValidateErrorsContainer onValidate={() => onShowCustomResultErrors(customResult.id)} />}
              <LcaCustomBiogenicResultsGrid title={title} epdVersionId={epdVersionId} customResultId={customResult.id} />
            </LcaGridErrorsContext.Provider>
          </FieldContainer>
        )}
        {[CUSTOM_RESULT_KEYS.BEST, CUSTOM_RESULT_KEYS.WORST].includes(customResult.resultType) && (
          <FieldContainer>
            <LcaGridErrorsContext.Provider value={shouldShowTableErrors && customResultError}>
              {customResultError && <ValidateErrorsContainer onValidate={() => onShowCustomResultErrors(customResult.id)} />}
              <LcaCustomBestWorstResultsGrid epdVersionId={epdVersionId} customResultId={customResult.id} />
            </LcaGridErrorsContext.Provider>
          </FieldContainer>
        )}
      </ToggleablePanel>
    </ErrorBoundary>
  );
};

export default LcaCustomResultPanel;
